import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Gem, 
  TrendingUp, 
  Building, 
  DollarSign, 
  MapPin, 
  Lightbulb,
  Calculator,
  Save,
  RefreshCw,
  Download,
  AlertTriangle,
  CheckCircle,
  Info,
  PiggyBank,
  FileText,
  Shield,
  Plus,
  X,
  Edit,
  Trash2,
  BarChart3,
  Calendar,
  Percent,
  Home,
  Ruler,
  Construction,
  Bell,
  Upload,
  Search
} from 'lucide-react';

// Komponenten-Imports
import CockpitDashboard from './investment-cockpit/components/CockpitDashboard';
import TransparencyTooltip from './investment-cockpit/components/TransparencyTooltip';
import MarktpreisSimulator from './investment-cockpit/components/MarktpreisSimulator_Professional';
import BaurechtTab from './investment-cockpit/components/BaurechtTab';
import FoerdermittelTab from './investment-cockpit/components/FoerdermittelTab';
import SanierungsTab from './investment-cockpit/components/SanierungsTab';

// masterSupabase für Datenbank-Zugriff
import { masterSupabase } from '../lib/supabase-master';
import FoerderMonitoring from './investment-cockpit/components/FoerderMonitoring';
import NewsLaufband from "../components/NewsLaufband";
import ExposeUpload from "./investment-cockpit/components/ExposeUpload";
import ObjectFinder from "./investment-cockpit/components/ObjectFinder";

// Hooks
import { useInvestmentCalculation } from './investment-cockpit/hooks/useInvestmentCalculation';

// Enhanced Themed Input Component - NO SCROLLBARS for number inputs
const ThemedInput = React.memo(({ field, label, type = "text", value, onChange, placeholder, ...props }) => (
  <div key={`input-${field}`} className="space-y-2">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 transition-all duration-200 ${
        type === 'number' ? 'appearance-none' : ''
      }`}
      style={type === 'number' ? { 
        MozAppearance: 'textfield',
        WebkitAppearance: 'none',
        margin: 0
      } : {}}
      {...props}
    />
    <style jsx>{`
      input[type=number]::-webkit-outer-spin-button,
      input[type=number]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type=number] {
        -moz-appearance: textfield;
      }
    `}</style>
  </div>
));

// Enhanced Themed Select Component - CONSISTENT STYLING
const ThemedSelect = React.memo(({ field, label, value, onChange, options, placeholder }) => (
  <div key={`select-${field}`} className="space-y-2">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
    </label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="w-full px-4 py-3 h-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 transition-all duration-200">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
        {options.map((option) => (
          <SelectItem 
            key={option.value} 
            value={option.value}
            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
));

const InvestmentCockpit = () => {
  // State Management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showTransparencyModal, setShowTransparencyModal] = useState(false);
  const [selectedKpiInfo, setSelectedKpiInfo] = useState(null);
  const [calculationName, setCalculationName] = useState('Neue Berechnung');
  const [showSecondLoan, setShowSecondLoan] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [editingAnalysisId, setEditingAnalysisId] = useState(null);
  const [editingName, setEditingName] = useState('');
  
  // PropertyData für MarktpreisSimulator
  const [propertyData, setPropertyData] = useState({
    address: '',
    size: 0,
    buildYear: 0,
    propertyType: '',
    condition: '',
    purchasePrice: 0
  });
  const { 
    calculationData, 
    calculations,
    kpis,
    isLoading,
    updateField,
    updateCalculation,
    saveCalculation,
    loadCalculation,
    fetchSavedCalculations,
    savedCalculations,
    currentCalculationId,
    deleteCalculation,
    updateCalculationName,
    getInsights
  } = useInvestmentCalculation();

  // Gespeicherte Berechnungen beim Start laden
  useEffect(() => {
    fetchSavedCalculations();
    loadPropertyData(); // Lade verfügbare Immobiliendaten
  }, [fetchSavedCalculations]);

  // PropertyData aus Clara360 Datenbank laden
  const loadPropertyData = async () => {
    try {
      const { data: properties, error } = await masterSupabase
        .from('properties')
        .select('address, size, build_year, property_type, condition, purchase_price')
        .limit(1);
      
      if (error) {
        console.error('Fehler beim Laden der Immobiliendaten:', error);
        return;
      }
      
      if (properties && properties.length > 0) {
        const property = properties[0];
        setPropertyData({
          address: property.address || 'Scheffelstraße 10, Ilvesheim',
          size: property.size || 120,
          buildYear: property.build_year || 1985,
          propertyType: property.property_type || 'Mehrfamilienhaus',
          condition: property.condition || 'Saniert',
          purchasePrice: property.purchase_price || 285000
        });
      } else {
        // Fallback für Demo-Zwecke mit Mannheimer Immobilie
        setPropertyData({
          address: 'Scheffelstraße 10, Ilvesheim',
          size: 120,
          buildYear: 1985,
          propertyType: 'Mehrfamilienhaus',
          condition: 'Saniert',
          purchasePrice: 285000
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Immobiliendaten:', error);
      // Fallback für Demo-Zwecke
      setPropertyData({
        address: 'Scheffelstraße 10, Ilvesheim',
        size: 120,
        buildYear: 1985,
        propertyType: 'Mehrfamilienhaus',
        condition: 'Saniert',
        purchasePrice: 285000
      });
    }
  };

  // Property Type Options
  const propertyTypeOptions = [
    { value: 'mehrfamilienhaus', label: 'Mehrfamilienhaus' },
    { value: 'einfamilienhaus', label: 'Einfamilienhaus' },
    { value: 'eigentumswohnung', label: 'Eigentumswohnung' },
    { value: 'gewerbeimmobilie', label: 'Gewerbeimmobilie' },
    { value: 'mixed_use', label: 'Mischnutzung' },
    { value: 'apartment_building', label: 'Apartmenthaus' },
    { value: 'villa', label: 'Villa' },
    { value: 'townhouse', label: 'Reihenhaus' },
    { value: 'other', label: 'Sonstiges' }
  ];

  // ENHANCED AREA CALCULATIONS
  const areaCalculations = useMemo(() => {
    const livingSpace = parseFloat(calculationData.living_space_sqm) || 0;
    const basementArea = parseFloat(calculationData.basement_area_sqm) || 0;
    const propertyArea = parseFloat(calculationData.property_area_sqm) || 0;
    const buildingArea = parseFloat(calculationData.building_area_sqm) || 0;

    // Automatic total area calculation
    const totalArea = livingSpace + basementArea;

    // Ratio calculations
    const livingToPropertyRatio = propertyArea > 0 ? (livingSpace / propertyArea) * 100 : 0;
    const livingToTotalRatio = totalArea > 0 ? (livingSpace / totalArea) * 100 : 0;
    const buildingToPropertyRatio = propertyArea > 0 ? (buildingArea / propertyArea) * 100 : 0;

    return {
      totalArea,
      livingToPropertyRatio,
      livingToTotalRatio,
      buildingToPropertyRatio
    };
  }, [calculationData.living_space_sqm, calculationData.basement_area_sqm, calculationData.property_area_sqm, calculationData.building_area_sqm]);

  // Memoized Input-Handler für Performance
  const inputHandlers = useMemo(() => {
    const handlers = {};
    const fields = [
      'purchase_price', 'purchase_costs_percent', 'renovation_costs', 'notary_costs',
      'equity', 'loan_amount', 'interest_rate', 'repayment_rate',
      'monthly_rent', 'monthly_operating_costs', 'vacancy_rate', 'annual_rent_increase',
      'property_tax', 'income_tax_rate', 'depreciation_rate',
      // Erweiterte Objektgrunddaten - EXAKTE SUPABASE FELDNAMEN
      'property_area_sqm', 'building_area_sqm', 'basement_area_sqm', 
      'number_of_units', 'property_type', 'number_of_rooms', 'number_of_bathrooms',
      // Zusätzliches Darlehen
      'loan2_amount', 'loan2_interest_rate', 'loan2_repayment_rate', 'loan2_term_years'
    ];

    fields.forEach(field => {
      handlers[field] = (value) => {
        updateField(field, value);
      };
    });

    return handlers;
  }, [updateField]);

  // Event Handler
  const handleSave = async () => {
    const result = await saveCalculation(calculationName);
    if (result.success) {
      console.log('Berechnung erfolgreich gespeichert');
    } else {
      console.error('Fehler beim Speichern:', result.error);
    }
  };

  const handleReset = () => {
    if (window.confirm('Alle Eingaben zurücksetzen?')) {
      updateCalculation({
        purchase_price: 0,
        purchase_costs_percent: 10,
        equity: 0,
        loan_amount: 0,
        interest_rate: 3.5,
        repayment_rate: 2,
        monthly_rent: 0,
        monthly_operating_costs: 0
      });
      setCalculationName('Neue Berechnung');
    }
  };

  const handleExportPDF = () => {
    // TODO: PDF Export implementieren
    console.log('PDF Export - noch nicht implementiert');
  };

  const handleLoadCalculation = async (calculationId) => {
    const result = await loadCalculation(calculationId);
    if (result.success) {
      setCalculationName(result.data.calculation_name);
      setShowAnalysisModal(false); // Modal schließen nach dem Laden
    }
  };

  const handleInfoClick = (kpiInfo) => {
    setSelectedKpiInfo(kpiInfo);
    setShowTransparencyModal(true);
  };

  const handleAnalysisClick = () => {
    setShowAnalysisModal(true);
  };

  const handleEditAnalysis = (id, currentName) => {
    setEditingAnalysisId(id);
    setEditingName(currentName);
  };

  const handleSaveEdit = async (id) => {
    const result = await updateCalculationName(id, editingName);
    if (result.success) {
      setEditingAnalysisId(null);
      setEditingName('');
    }
  };

  const handleCancelEdit = () => {
    setEditingAnalysisId(null);
    setEditingName('');
  };

  const handleDeleteAnalysis = async (id) => {
    if (window.confirm('Sind Sie sicher, dass Sie diese Analyse löschen möchten?')) {
      const result = await deleteCalculation(id);
      if (result.success) {
        console.log('Analyse erfolgreich gelöscht');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="container mx-auto px-4 py-8">
        {/* CLEANED HEADER WITH CONSISTENT BUTTON SIZES */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Gem className="h-8 w-8 text-blue-600" />
                Investment Cockpit ULTIMATE
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Professionelle Immobilien-Investment-Analyse mit erweiterten Objektdaten und Live-Insights
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={handleSave}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-2 h-10"
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? 'Speichert...' : 'Speichern'}
              </Button>
              <Button 
                onClick={handleExportPDF}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 h-10"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>


        {/* News-Laufband für Förder-Updates - Neue Position */}
        <div className="mb-6">
          <NewsLaufband />
        </div>
        {/* ORIGINAL TABS DESIGN - PRESERVED */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Tab Navigation - ORIGINAL DESIGN */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'dashboard'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <button
              onClick={() => setActiveTab('input')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'input'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <span className="hidden sm:inline">Objektanalyse</span>
              <FileText className="h-4 w-4" />
            </button>
            <button
              onClick={() => setActiveTab('expose-upload')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'expose-upload'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Exposé-Upload</span>
            </button>
            <button
              onClick={() => setActiveTab('object-finder')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'object-finder'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">KI-Objektfinder</span>
            </button>
            <button
              onClick={() => setActiveTab('building-rights')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'building-rights'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Baurecht</span>
            </button>
            <button
              onClick={() => setActiveTab('funding')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'funding'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Förderung</span>
            </button>
            <button
              onClick={() => setActiveTab('location')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'location'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Construction className="h-4 w-4" />
              <span className="hidden sm:inline">Sanierung</span>
            </button>
            <button
              onClick={() => setActiveTab('monitoring')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'monitoring'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Förder-Updates</span>
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === 'insights'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Lightbulb className="h-4 w-4" />
              <span className="hidden sm:inline">Marktpreis-Simulator</span>
            </button>
          </div>

          {/* TAB CONTENT */}
          {activeTab === 'dashboard' && (
            <CockpitDashboard
              kpis={kpis}
              calculationData={calculationData}
              selectedProperty={selectedProperty}
              onInfoClick={handleInfoClick}
              savedCalculations={savedCalculations}
              currentCalculationId={currentCalculationId}
              onAnalysisClick={handleAnalysisClick}
            />
          )}

          {activeTab === 'object-finder' && (
            <ObjectFinder 
              onAnalyzeProperty={(data) => {
                // Auto-fill property data into calculation
                setCalculationData(prevData => ({
                  ...prevData,
                  purchasePrice: data.purchasePrice || prevData.purchasePrice,
                  livingSpace: data.livingSpace || prevData.livingSpace,
                  rooms: data.rooms || prevData.rooms,
                  yearBuilt: data.yearBuilt || prevData.yearBuilt,
                  monthlyRent: data.monthlyRent || prevData.monthlyRent,
                  address: data.address || prevData.address,
                  propertyType: data.propertyType || prevData.propertyType
                }));
                // Switch to input tab to show filled data
                setActiveTab('input');
              }}
            />
          )}

          {/* ULTIMATE 2/3 + 1/3 LAYOUT FOR INPUT TAB WITH ENHANCED FEATURES */}
          {activeTab === 'input' && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* LEFT SIDE (2/3) - INPUT FORMS WITH EXTENDED PROPERTY DATA */}
              <div className="xl:col-span-2 space-y-6">
                
                {/* ANALYSE-TITEL */}
                <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-purple-200 dark:border-purple-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-gray-900 dark:text-gray-100">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Analyse-Name
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ThemedInput
                      field="calculation_name"
                      label="Name der Investment-Analyse"
                      type="text"
                      value={calculationName}
                      onChange={setCalculationName}
                      placeholder="z.B. Mehrfamilienhaus München 2025"
                    />
                  </CardContent>
                </Card>

                {/* ERWEITERTE OBJEKT-GRUNDDATEN */}
                <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-gray-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-gray-900 dark:text-gray-100">
                      <Building className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Objekt-Grunddaten
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Grundlegende Daten */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ThemedInput
                        field="purchase_price"
                        label="Kaufpreis (€)"
                        type="number"
                        value={calculationData.purchase_price}
                        onChange={inputHandlers.purchase_price}
                        placeholder="450.000"
                      />
                      <ThemedInput
                        field="purchase_costs_percent"
                        label="Kaufnebenkosten (%)"
                        type="number"
                        value={calculationData.purchase_costs_percent}
                        onChange={inputHandlers.purchase_costs_percent}
                        placeholder="10"
                        step="0.1"
                      />
                      <ThemedInput
                        field="construction_year"
                        label="Baujahr"
                        type="number"
                        value={calculationData.construction_year}
                        onChange={(value) => updateField('construction_year', value)}
                        placeholder="2000"
                      />
                      <ThemedSelect
                        field="property_type"
                        label="Objekttyp"
                        value={calculationData.property_type}
                        onChange={inputHandlers.property_type}
                        options={propertyTypeOptions}
                        placeholder="Objekttyp auswählen"
                      />
                    </div>

                    {/* Flächenangaben */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                        <Ruler className="h-4 w-4" />
                        Flächenangaben
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ThemedInput
                          field="property_area_sqm"
                          label="Grundstücksfläche (m²)"
                          type="number"
                          value={calculationData.property_area_sqm}
                          onChange={inputHandlers.property_area_sqm}
                          placeholder="800"
                          step="0.1"
                        />
                        <ThemedInput
                          field="building_area_sqm"
                          label="Gebäudegrundfläche (m²)"
                          type="number"
                          value={calculationData.building_area_sqm}
                          onChange={inputHandlers.building_area_sqm}
                          placeholder="200"
                          step="0.1"
                        />
                        <ThemedInput
                          field="living_space_sqm"
                          label="Wohnfläche (m²)"
                          type="number"
                          value={calculationData.living_space_sqm}
                          onChange={(value) => updateField('living_space_sqm', value)}
                          placeholder="150"
                          step="0.1"
                        />
                        <ThemedInput
                          field="basement_area_sqm"
                          label="Keller-/Nutzfläche (m²)"
                          type="number"
                          value={calculationData.basement_area_sqm}
                          onChange={inputHandlers.basement_area_sqm}
                          placeholder="50"
                          step="0.1"
                        />
                      </div>
                    </div>

                    {/* Ausstattung */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Ausstattung
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <ThemedInput
                          field="number_of_units"
                          label="Anzahl Einheiten/Wohnungen"
                          type="number"
                          value={calculationData.number_of_units}
                          onChange={inputHandlers.number_of_units}
                          placeholder="4"
                        />
                        <ThemedInput
                          field="number_of_rooms"
                          label="Anzahl Zimmer"
                          type="number"
                          value={calculationData.number_of_rooms}
                          onChange={inputHandlers.number_of_rooms}
                          placeholder="12"
                          step="0.5"
                        />
                        <ThemedInput
                          field="number_of_bathrooms"
                          label="Anzahl Badezimmer"
                          type="number"
                          value={calculationData.number_of_bathrooms}
                          onChange={inputHandlers.number_of_bathrooms}
                          placeholder="4"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* FINANZIERUNG */}
                <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-gray-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-gray-900 dark:text-gray-100">
                      <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                      Finanzierung
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ThemedInput
                        field="equity"
                        label="Eigenkapital (€)"
                        type="number"
                        value={calculationData.equity}
                        onChange={inputHandlers.equity}
                        placeholder="100000"
                      />
                      <ThemedInput
                        field="loan_amount"
                        label="Darlehensbetrag (€)"
                        type="number"
                        value={calculationData.loan_amount}
                        onChange={inputHandlers.loan_amount}
                        placeholder="400000"
                      />
                      <ThemedInput
                        field="interest_rate"
                        label="Zinssatz (%)"
                        type="number"
                        value={calculationData.interest_rate}
                        onChange={inputHandlers.interest_rate}
                        placeholder="3.5"
                        step="0.01"
                      />
                      <ThemedInput
                        field="repayment_rate"
                        label="Tilgungsrate (%)"
                        type="number"
                        value={calculationData.repayment_rate}
                        onChange={inputHandlers.repayment_rate}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* MIETEINNAHMEN & KOSTEN */}
                <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-gray-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-gray-900 dark:text-gray-100">
                      <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Mieteinnahmen & Kosten
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ThemedInput
                        field="monthly_rent"
                        label="Monatliche Miete (€)"
                        type="number"
                        value={calculationData.monthly_rent}
                        onChange={(value) => updateField('monthly_rent', value)}
                        placeholder="1.800"
                      />
                      <ThemedInput
                        field="utilities_monthly"
                        label="Nebenkosten monatlich (€)"
                        type="number"
                        value={calculationData.utilities_monthly}
                        onChange={(value) => updateField('utilities_monthly', value)}
                        placeholder="200"
                      />
                      <ThemedInput
                        field="management_costs_monthly"
                        label="Verwaltungskosten monatlich (€)"
                        type="number"
                        value={calculationData.management_costs_monthly}
                        onChange={(value) => updateField('management_costs_monthly', value)}
                        placeholder="100"
                      />
                      <ThemedInput
                        field="maintenance_reserve_monthly"
                        label="Instandhaltungsrücklage monatlich (€)"
                        type="number"
                        value={calculationData.maintenance_reserve_monthly}
                        onChange={(value) => updateField('maintenance_reserve_monthly', value)}
                        placeholder="150"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* STEUERN & ABSCHREIBUNGEN */}
                <Card className="bg-white dark:bg-slate-900 border-gray-200 dark:border-gray-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-gray-900 dark:text-gray-100">
                      <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
                      Steuern & Abschreibungen
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ThemedInput
                        field="tax_rate_percent"
                        label="Steuersatz (%)"
                        type="number"
                        value={calculationData.tax_rate_percent}
                        onChange={(value) => updateField('tax_rate_percent', value)}
                        placeholder="42"
                      />
                      <ThemedInput
                        field="depreciation_rate_percent"
                        label="AfA-Satz (%)"
                        type="number"
                        value={calculationData.depreciation_rate_percent}
                        onChange={(value) => updateField('depreciation_rate_percent', value)}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* RIGHT SIDE (1/3) - ENHANCED LIVE INSIGHTS WITH CONSISTENT SPACING */}
              <div className="space-y-6">
                {/* ENHANCED OBJEKT-ÜBERSICHT WITH AREA CALCULATIONS */}
                <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 shadow-sm sticky top-6">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-indigo-900 dark:text-indigo-100">
                      <Building className="h-5 w-5" />
                      Objekt-Übersicht
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-indigo-200 dark:border-indigo-700">
                        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Objekttyp:</span>
                        <span className="font-bold text-indigo-900 dark:text-indigo-100">
                          {calculationData.property_type ? 
                            propertyTypeOptions.find(opt => opt.value === calculationData.property_type)?.label || 'N/A' 
                            : 'Nicht angegeben'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-indigo-200 dark:border-indigo-700">
                        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Wohnfläche:</span>
                        <span className="font-bold text-indigo-900 dark:text-indigo-100">
                          {calculationData.living_space_sqm ? `${calculationData.living_space_sqm} m²` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-indigo-200 dark:border-indigo-700">
                        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Gesamtfläche:</span>
                        <span className="font-bold text-indigo-900 dark:text-indigo-100">
                          {areaCalculations.totalArea > 0 ? `${areaCalculations.totalArea.toFixed(1)} m²` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-indigo-200 dark:border-indigo-700">
                        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Einheiten:</span>
                        <span className="font-bold text-indigo-900 dark:text-indigo-100">
                          {calculationData.number_of_units || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-indigo-200 dark:border-indigo-700">
                        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Preis/m²:</span>
                        <span className="font-bold text-indigo-900 dark:text-indigo-100">
                          {calculationData.purchase_price && calculationData.living_space_sqm ? 
                            `${Math.round(calculationData.purchase_price / calculationData.living_space_sqm).toLocaleString()}€` : 'N/A'}
                        </span>
                      </div>
                      
                      {/* NEW AREA RATIO CALCULATIONS */}
                      <div className="border-t border-indigo-200 dark:border-indigo-700 pt-3 mt-3">
                        <h5 className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-2">Flächenverhältnisse</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Wohn- zu Grundstück:</span>
                            <span className="font-bold text-indigo-900 dark:text-indigo-100 text-sm">
                              {areaCalculations.livingToPropertyRatio > 0 ? `${areaCalculations.livingToPropertyRatio.toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Wohn- zu Gesamtfläche:</span>
                            <span className="font-bold text-indigo-900 dark:text-indigo-100 text-sm">
                              {areaCalculations.livingToTotalRatio > 0 ? `${areaCalculations.livingToTotalRatio.toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Grundstücksausnutzung:</span>
                            <span className="font-bold text-indigo-900 dark:text-indigo-100 text-sm">
                              {areaCalculations.buildingToPropertyRatio > 0 ? `${areaCalculations.buildingToPropertyRatio.toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* RENDITE-KENNZAHLEN - CONSISTENT SPACING */}
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-blue-900 dark:text-blue-100">
                      <Calculator className="h-5 w-5" />
                      Rendite-Analyse
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700" title="Jahresmiete ÷ Kaufpreis × 100">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Bruttomietrendite:
                          <span className="ml-1 text-xs opacity-70">💡</span>
                        </span>
                        <span className="font-bold text-blue-900 dark:text-blue-100">
                          {calculations?.gross_rental_yield ? `${calculations.gross_rental_yield.toFixed(2)}%` : '0.00%'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700" title="(Jahresmiete - Betriebskosten) ÷ Kaufpreis × 100">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Nettomietrendite:
                          <span className="ml-1 text-xs opacity-70">💡</span>
                        </span>
                        <span className="font-bold text-blue-900 dark:text-blue-100">
                          {calculations?.net_rental_yield ? `${calculations.net_rental_yield.toFixed(2)}%` : '0.00%'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700" title="Jährlicher Cashflow ÷ Eigenkapital × 100">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          Eigenkapitalrendite:
                          <span className="ml-1 text-xs opacity-70">💡</span>
                        </span>
                        <span className="font-bold text-blue-900 dark:text-blue-100">
                          {calculations?.equity_return ? `${calculations.equity_return.toFixed(2)}%` : '0.00%'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* CASHFLOW-ANALYSE - CONSISTENT SPACING */}
                <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-green-900 dark:text-green-100">
                      <TrendingUp className="h-5 w-5" />
                      Cashflow-Analyse
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700" title="Miete - Betriebskosten - Finanzierungskosten">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">
                          Monatl. Cashflow:
                          <span className="ml-1 text-xs opacity-70">💡</span>
                        </span>
                        <span className={`font-bold ${(calculations?.monthly_cashflow || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {calculations?.monthly_cashflow ? `${calculations.monthly_cashflow.toFixed(0)}€` : '0€'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">Jährl. Cashflow:</span>
                        <span className={`font-bold ${(calculations?.monthly_cashflow || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {calculations?.monthly_cashflow ? `${(calculations.monthly_cashflow * 12).toFixed(0)}€` : '0€'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2" title={`Nach Steuern (bei ${calculationData.tax_rate_percent || 42}% Steuersatz)`}>
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">
                          Nach Steuern:
                          <span className="ml-1 text-xs opacity-70">💡</span>
                        </span>
                        <span className={`font-bold ${(kpis?.annualCashflowAfterTax || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {kpis?.annualCashflowAfterTax ? `${kpis.annualCashflowAfterTax.toFixed(0)}€` : '0€'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* INVESTMENT-ÜBERSICHT - CONSISTENT SPACING */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-3 text-lg text-purple-900 dark:text-purple-100">
                      <PiggyBank className="h-5 w-5" />
                      Investment-Übersicht
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-700">
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Gesamtinvestition:</span>
                        <span className="font-bold text-purple-900 dark:text-purple-100">
                          {kpis?.totalInvestment ? `${kpis.totalInvestment.toLocaleString()}€` : '0€'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-700">
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Eigenkapitalquote:</span>
                        <span className="font-bold text-purple-900 dark:text-purple-100">
                          {kpis?.equityPercent ? `${kpis.equityPercent.toFixed(1)}%` : '0.0%'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Monatl. Belastung:</span>
                        <span className="font-bold text-purple-900 dark:text-purple-100">
                          {kpis?.totalMonthlyLoanPayment ? `${kpis.totalMonthlyLoanPayment.toLocaleString()}€` : '0€'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}


          {activeTab === 'expose-upload' && (
            <ExposeUpload onDataExtracted={(data) => console.log('Extracted data:', data)} />
          )}
          {activeTab === 'building-rights' && (
            <BaurechtTab calculationData={calculationData} />
          )}

          {activeTab === 'funding' && (
            <FoerdermittelTab calculationData={calculationData} />
          )}

          {activeTab === 'location' && (
            <SanierungsTab />
          )}

          {activeTab === 'monitoring' && (
            <FoerderMonitoring />
          )}

          {activeTab === 'insights' && (
            <div className="space-y-6">
              <MarktpreisSimulator 
                propertyData={propertyData} 
                onMarketValueUpdate={(data) => console.log('Market value updated:', data)} 
              />
            </div>
          )}
        </Tabs>

        {/* MODALS */}
        {showTransparencyModal && (
          <TransparencyTooltip
            isOpen={showTransparencyModal}
            onClose={() => setShowTransparencyModal(false)}
            kpiInfo={selectedKpiInfo}
          />
        )}

        {/* ENHANCED Analysis Management Modal */}
        {showAnalysisModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Gespeicherte Analysen verwalten
                </h2>
                <button
                  onClick={() => setShowAnalysisModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {savedCalculations.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Noch keine Analysen gespeichert
                  </p>
                ) : (
                  <div className="grid gap-4">
                    {savedCalculations.map((calc) => (
                      <div
                        key={calc.id}
                        onClick={() => handleLoadCalculation(calc.id)}
                        className={`p-6 border rounded-xl cursor-pointer transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 ${
                          calc.id === currentCalculationId
                            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                            : 'border-gray-200 dark:border-gray-600 hover:shadow-md'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {editingAnalysisId === calc.id ? (
                              <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-lg font-semibold bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                                  autoFocus
                                />
                                <Button
                                  onClick={() => handleSaveEdit(calc.id)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  onClick={handleCancelEdit}
                                  size="sm"
                                  variant="outline"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-3 mb-3">
                                  <h3 className="font-bold text-xl text-gray-900 dark:text-white">
                                    {calc.calculation_name}
                                  </h3>
                                  {calc.id === currentCalculationId && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                      Aktiv
                                    </span>
                                  )}
                                </div>
                                
                                {/* ENHANCED INFORMATION GRID WITH NEW FIELDS */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                  <div className="flex items-center gap-2 text-sm">
                                    <Building className="h-4 w-4 text-blue-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Kaufpreis:</span>
                                    <span className="font-semibold">€{calc.purchase_price?.toLocaleString() || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Home className="h-4 w-4 text-indigo-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Objekttyp:</span>
                                    <span className="font-semibold">
                                      {calc.property_type ? 
                                        propertyTypeOptions.find(opt => opt.value === calc.property_type)?.label || calc.property_type 
                                        : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Ruler className="h-4 w-4 text-green-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Wohnfläche:</span>
                                    <span className="font-semibold">{calc.living_space_sqm ? `${calc.living_space_sqm} m²` : 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <DollarSign className="h-4 w-4 text-green-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Monatliche Miete:</span>
                                    <span className="font-semibold">€{calc.monthly_rent?.toLocaleString() || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Percent className="h-4 w-4 text-purple-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Nettomietrendite:</span>
                                    <span className="font-semibold">{calc.net_rental_yield?.toFixed(2) || 'N/A'}%</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <BarChart3 className="h-4 w-4 text-emerald-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Monatlicher Cashflow:</span>
                                    <span className={`font-semibold ${(calc.monthly_cashflow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      €{calc.monthly_cashflow?.toLocaleString() || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="h-4 w-4 text-gray-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Erstellt:</span>
                                    <span className="font-semibold">{new Date(calc.created_at).toLocaleDateString('de-DE')}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <TrendingUp className="h-4 w-4 text-orange-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Eigenkapitalrendite:</span>
                                    <span className="font-semibold">{calc.equity_return?.toFixed(2) || 'N/A'}%</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Building className="h-4 w-4 text-blue-600" />
                                    <span className="text-gray-600 dark:text-gray-400">Einheiten:</span>
                                    <span className="font-semibold">{calc.number_of_units || 'N/A'}</span>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* ACTION BUTTONS */}
                          <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                            <Button
                              onClick={() => handleEditAnalysis(calc.id, calc.calculation_name)}
                              size="sm"
                              variant="outline"
                              className="hover:bg-blue-50 hover:border-blue-300"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteAnalysis(calc.id)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestmentCockpit;

